import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/authContext';
import { updateUserProfile, getUserById } from '../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import BackgroundInfoModal from '../components/BackgroundInfoModal';
import '../styles/styles.css';

const ProfilePage = () => {
    const { user, userProfile, refreshUserProfile } = useAuth();
    const { userId } = useParams();
    const navigate = useNavigate();
    
    const [profileData, setProfileData] = useState({ 
        displayName: '', 
        email: '',
        isPublic: false,
        backgroundInfo: {
            allergies: '',
            dislikes: '',
            likes: '',
            generalInfo: '',
            privacyAcknowledged: false
        }
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    const isOwnProfile = !userId || userId === user?.uid;

    useEffect(() => {
        const loadProfileData = async () => {
            try {
                setLoading(true);
                
                if (isOwnProfile && user) {
                    // For own profile, use the userProfile from auth context
                    setProfileData({ 
                        displayName: userProfile?.display_name || userProfile?.username || user.displayName || '', 
                        email: userProfile?.email || userProfile?.email || user.email || '',
                        isPublic: userProfile?.is_public || false,
                        backgroundInfo: userProfile?.background_info || {
                            allergies: '',
                            dislikes: '',
                            likes: '',
                            generalInfo: '',
                            privacyAcknowledged: false
                        }
                    });
                } else if (userId) {
                    // For other users, fetch from backend
                    try {
                        const data = await getUserById(userId);
                        setProfileData({ 
                            displayName: data.display_name || data.username || '', 
                            email: data.email || '',
                            isPublic: data.is_public || false,
                            backgroundInfo: data.background_info || {
                                allergies: '',
                                dislikes: '',
                                likes: '',
                                generalInfo: '',
                                privacyAcknowledged: false
                            }
                        });
                    } catch (err) {
                        console.error('Failed to fetch user profile:', err);
                        setProfileData({ 
                            displayName: 'Unknown User', 
                            email: '',
                            isPublic: false,
                            backgroundInfo: {
                                allergies: '',
                                dislikes: '',
                                likes: '',
                                generalInfo: '',
                                privacyAcknowledged: false
                            }
                        });
                    }
                }
            } catch (err) {
                console.error('Failed to load profile:', err);
                setError('Failed to load user profile.');
            } finally {
                setLoading(false);
            }
        };

        if (user || userId) {
            loadProfileData();
        }
    }, [userId, user, userProfile, isOwnProfile]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const updateData = { 
            display_name: profileData.displayName,
            is_public: profileData.isPublic,
            background_info: profileData.backgroundInfo
        };

        console.log('📝 Sending profile update:', updateData);

        try {
            const token = await user.getIdToken();
            const result = await updateUserProfile(user.uid, updateData, token);
            
            // Refresh the user profile in auth context
            await refreshUserProfile();
            
            // Show success message with number of recipes updated
            const recipesUpdated = result.recipesUpdated || 0;
            setSuccess(`Profile updated successfully! ${recipesUpdated > 0 ? `${recipesUpdated} recipes updated with your new display name.` : ''}`);
            
        } catch (err) {
            console.error('Failed to update profile:', err);
            setError('Failed to update profile. Please try again.');
        }
    };

    const handleBackgroundInfoSave = (backgroundInfo) => {
        setProfileData(prev => ({
            ...prev,
            backgroundInfo
        }));
    };

    const hasBackgroundInfo = () => {
        const info = profileData.backgroundInfo;
        return info.allergies || info.dislikes || info.likes || info.generalInfo;
    };

    if (loading) {
        return <div className="text-center py-2">Loading profile...</div>;
    }

    if (!user && isOwnProfile) {
        return <div className="error-message compact">Please log in to view your profile.</div>;
    }

    return (
        <div className="create-recipe-container">
            <h1 className="text-center" style={{ 
                marginBottom: 'var(--spacing-xl)', 
                marginTop: 'var(--spacing-xl)',
                fontFamily: 'var(--font-heading)',
                fontSize: '2.5rem',
                fontWeight: '700',
                color: 'var(--color-black)'
            }}>
                {isOwnProfile ? 'Your Profile' : `${profileData.displayName}'s Profile`}
            </h1>
            
            <form onSubmit={handleSubmit} className="create-recipe-form">
                <div className="form-section">
                    <h3 className="section-title">Basic Information</h3>
                    
                    <div className="form-group">
                        <label className="form-label" htmlFor="displayName">Display Name</label>
                        <input
                            type="text"
                            id="displayName"
                            name="displayName"
                            className="form-input"
                            value={profileData.displayName}
                            onChange={(e) => setProfileData(prev => ({ ...prev, displayName: e.target.value }))}
                            disabled={!isOwnProfile}
                            placeholder="Enter your display name"
                        />
                        <small style={{ color: 'var(--color-medium-gray)', fontSize: '0.8rem' }}>
                            This will be used as your name on recipes you create.
                        </small>
                    </div>
                    
                    <div className="form-group">
                        <label className="form-label" htmlFor="email">Email (Read Only)</label>
                        <input
                            type="email"
                            id="email"
                            className="form-input"
                            value={profileData.email}
                            disabled={true}
                            placeholder="Email address"
                            style={{ backgroundColor: 'var(--color-light-gray)', cursor: 'not-allowed' }}
                        />
                        <small style={{ color: 'var(--color-medium-gray)', fontSize: '0.8rem' }}>
                            Email cannot be changed. Contact support if you need to update your email address.
                        </small>
                    </div>
                </div>

                <div className="form-section">
                    <h3 className="section-title">Privacy Settings</h3>
                    
                    <div className="form-group">
                        <div className="privacy-toggle-container">
                            <label className="privacy-toggle-label">
                                <span className="privacy-toggle-text">Make my profile public</span>
                                <div className="privacy-toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={profileData.isPublic}
                                        onChange={(e) => setProfileData(prev => ({ ...prev, isPublic: e.target.checked }))}
                                        disabled={!isOwnProfile}
                                        className="privacy-toggle-input"
                                    />
                                    <span className="privacy-toggle-slider"></span>
                                </div>
                            </label>
                            <small style={{ color: 'var(--color-medium-gray)', fontSize: '0.8rem', display: 'block', marginTop: '0.5rem' }}>
                                {profileData.isPublic 
                                    ? 'Your profile and recipes will be visible to other users.' 
                                    : 'Your profile will only be visible to you.'}
                            </small>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3 className="section-title">Personal Info</h3>
                    
                    <div className="background-info-summary">
                        {hasBackgroundInfo() ? (
                            <div className="info-preview">
                                <h4>Current Information:</h4>
                                {profileData.backgroundInfo.allergies && (
                                    <div className="info-item">
                                        <strong>Allergies:</strong> {profileData.backgroundInfo.allergies}
                                    </div>
                                )}
                                {profileData.backgroundInfo.dislikes && (
                                    <div className="info-item">
                                        <strong>Dislikes:</strong> {profileData.backgroundInfo.dislikes}
                                    </div>
                                )}
                                {profileData.backgroundInfo.likes && (
                                    <div className="info-item">
                                        <strong>Preferences:</strong> {profileData.backgroundInfo.likes}
                                    </div>
                                )}
                                {profileData.backgroundInfo.generalInfo && (
                                    <div className="info-item">
                                        <strong>Additional Info:</strong> {profileData.backgroundInfo.generalInfo}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="no-info">
                                <p>No personal information provided yet.</p>
                            </div>
                        )}
                    </div>
                    
                    {isOwnProfile && (
                        <button 
                            type="button" 
                            className="btn-secondary"
                            onClick={() => setIsModalOpen(true)}
                            style={{ marginTop: '1rem' }}
                        >
                            {hasBackgroundInfo() ? 'Edit Personal Info' : 'Add Personal Info'}
                        </button>
                    )}
                    
                    <small style={{ color: 'var(--color-medium-gray)', fontSize: '0.8rem', display: 'block', marginTop: '0.5rem' }}>
                        This information helps us provide better recipe recommendations and can be shared with other users if your profile is public.
                    </small>
                </div>

                {error && <div className="error-message compact">{error}</div>}
                {success && <div className="text-center" style={{color: 'green', marginBottom: 'var(--spacing-md)'}}>{success}</div>}
                
                {isOwnProfile && (
                    <button type="submit" className="create-submit-button">
                        Save Changes
                    </button>
                )}
            </form>

            <BackgroundInfoModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleBackgroundInfoSave}
                currentInfo={profileData.backgroundInfo}
            />
        </div>
    );
};

export default ProfilePage; 